import type {
  NativeQuote,
  NativeQuoteRequest,
} from "@/modules/dashboard/dispersions/services/paymentsQuotes.service";
import { isActivatableWalletCurrency } from "@/modules/dashboard/wallets/constants/wallets.constants";
import type { RuntimeWallet } from "@/modules/dashboard/wallets/types/payments-wallets.types";

/**
 * Converting between two of the organization's own wallets. The quote prices it (`service: "swap"`): the
 * conversion fee and the swap service fee come back summed in `fees.total`, in the origin currency, and the
 * rate carries no spread — so `receive = (send − fee) × mid` and the screen shows one "commission" next to
 * the plain rate, never a breakdown the backend does not have.
 */

/** Which amount the person typed: what leaves the origin wallet, or what must land in the destination. */
export type SwapAnchor = "send" | "receive";

export function swapQuoteRequest(input: {
  originCurrency: string;
  destinationCurrency: string;
  amount: string;
  anchor: SwapAnchor;
}): NativeQuoteRequest {
  return {
    origin_currency: input.originCurrency,
    destination_currency: input.destinationCurrency,
    service: "swap",
    ...(input.anchor === "send" ? { send_amount: input.amount } : { receive_amount: input.amount }),
  };
}

/** Whether a quote still prices the conversion on screen; a stale one must never be redeemed. */
export function quoteMatchesSwap(
  quote: NativeQuote,
  originCurrency: string,
  destinationCurrency: string,
): boolean {
  return (
    quote.service === "swap" &&
    quote.currency.from === originCurrency &&
    quote.currency.to === destinationCurrency
  );
}

export interface SwapBreakdown {
  /** Leaves the origin wallet, fee included. */
  debit: string;
  /** Conversion and service fee together, in the origin currency. */
  fee: string;
  /** Lands in the destination wallet. */
  credit: string;
}

export function swapBreakdown(quote: NativeQuote): SwapBreakdown {
  return { debit: quote.amounts.send, fee: quote.fees.total, credit: quote.amounts.receive };
}

/**
 * The rate the conversion used, one unit of the stronger currency in the weaker one ("1 BRL = 746,27 COP").
 * Read off the amounts (`receive ÷ net`), not by inverting `rate.effective`: that is cut at 8 decimals, which
 * leaves about five significant digits on a pair like COP→USD. Display only — no amount is derived from it.
 */
export function swapReadableRate(quote: NativeQuote): { base: string; quote: string; value: number } | null {
  const net = Number(quote.amounts.net);
  const received = Number(quote.amounts.receive);
  if (!(net > 0) || !(received > 0)) return null;
  const perOrigin = received / net;
  return perOrigin >= 1
    ? { base: quote.currency.from, quote: quote.currency.to, value: perOrigin }
    : { base: quote.currency.to, quote: quote.currency.from, value: net / received };
}

export interface SwapDestination {
  wallet: RuntimeWallet;
  /** A frozen wallet would take the money and lock it: listed, never selectable. */
  disabled: boolean;
}

/**
 * Where the money can go: the organization's OTHER active wallets that the wallets home shows. A frozen one
 * is listed but disabled — the backend credits it anyway and the money lands immobilized. A closed one, or a
 * withheld currency (USD, …) whose wallet the home hides, is not offered.
 */
export function swapDestinations(
  wallets: readonly RuntimeWallet[],
  origin: RuntimeWallet,
): SwapDestination[] {
  return wallets
    .filter(
      (wallet) =>
        wallet.id !== origin.id &&
        wallet.currency !== origin.currency &&
        wallet.status !== "closed" &&
        isActivatableWalletCurrency(wallet.currency),
    )
    .map((wallet) => ({ wallet, disabled: wallet.status !== "active" }));
}

/** The wallets money can leave from: every active one. */
export const swapOrigins = (wallets: readonly RuntimeWallet[]): RuntimeWallet[] =>
  wallets.filter((wallet) => wallet.status === "active");

/**
 * Whether "Convertir" is offered on a wallet's card: it must be active and have at least one active wallet to
 * convert into. An organization with a single active wallet sees no button.
 */
export function canConvertFrom(wallet: RuntimeWallet, wallets: readonly RuntimeWallet[]): boolean {
  return (
    wallet.status === "active" &&
    swapDestinations(wallets, wallet).some((destination) => !destination.disabled)
  );
}
